import { inject, injectable } from 'inversify';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { FileUpload } from '../models/file-upload';
import { ApiConfig } from './api-config';
import { FileManagerService } from './file-manager.service';
import { LightHttp } from './light-http.service';
import { LoginService } from './login.service';

export interface WorkflowSearchResult {
    workflowId: string;
    uuid: string;
    fromType: string;
    toType: string;
    processType: string;
    featurePage: string;
    createdDate: string;
    failed: boolean;
    uploadedFiles: Array<{
        filename: string;
        readableFilename: string;
        size: number;
    }>;
}

export interface WorkflowRecreateResponse {
    workflowId: string;
    uuid: string;
    fromType: string;
    toType: string;
    processType: string;
    featurePage: string;
    failed: boolean;
    options: string;           // JSON string — must JSON.parse()
    optionsSnapshot: string;   // JSON string — must JSON.parse()
    originalInputFiles: string; // JSON string — must JSON.parse()
    metadataList: string;      // JSON string — must JSON.parse()
    fileUploadedServerEndpointList: string[];
    uploadedFiles: Array<{
        id: string;
        edgeId: string;
        filename: string;
        readableFilename: string;
        size: number;
    }>;
}

@injectable()
export class WorkflowRecreateService {

    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(LoginService) private loginService: LoginService,
        @inject(FileManagerService) private fileManagerService: FileManagerService,
    ) {}

    get isAdmin(): boolean {
        return this.loginService.userDetails?.admin === true;
    }

    searchWorkflows(query: string): Observable<WorkflowSearchResult[]> {
        if (!this.isAdmin) return of([]);
        const url = this.apiConfig.getAdminWorkflowSearchEndpoint();
        return this.http.get<WorkflowSearchResult[]>(url, { params: { q: query } }).pipe(
            catchError(() => of([])),
        );
    }

    recreateWorkflow(workflowId: string): Observable<FileUpload> {
        if (!this.isAdmin) return throwError(() => new Error('Admin access required'));
        const url = this.apiConfig.getAdminWorkflowRecreateEndpoint(workflowId);
        return this.http.get<WorkflowRecreateResponse>(url).pipe(
            map(response => this.buildFileUpload(response)),
        );
    }

    private buildFileUpload(response: WorkflowRecreateResponse): FileUpload {
        const options = this.safeParse(response.options, {});
        const optionsSnapshot = this.safeParse(response.optionsSnapshot, {});
        const originalInputFiles = this.safeParse(response.originalInputFiles, []);
        const metadataList = this.safeParse(response.metadataList, []);

        const fileUpload = new FileUpload();
        fileUpload.id = response.uuid;
        fileUpload.fromType = [response.fromType] as any;
        fileUpload.toType = response.toType as any;
        fileUpload.processType = response.processType as any;
        fileUpload.options = options;
        fileUpload.optionsSnapshot = optionsSnapshot;
        fileUpload.originalInputFiles = originalInputFiles;
        fileUpload.metadataList = metadataList;
        fileUpload.endpointData = metadataList[0] || null;
        fileUpload.name = response.uploadedFiles[0]?.readableFilename || 'file';
        fileUpload.size = response.uploadedFiles[0]?.size || 0;
        fileUpload.status = 'uploaded' as any;
        fileUpload.pageData = { path: response.featurePage } as any;
        fileUpload.orientation = options?.orientationAngleList;
        // processFile() calls updateProcessDomain() which accesses file[0].name.
        // Create a minimal File so it doesn't crash. Actual bytes are on ironman.
        fileUpload.file = [new File([], response.uploadedFiles[0]?.readableFilename || 'file')];

        return fileUpload;
    }

    private safeParse(jsonString: string, fallback: any): any {
        if (!jsonString) return fallback;
        try {
            return JSON.parse(jsonString);
        } catch {
            return fallback;
        }
    }
}
